import { Col, Row, Space, Radio, RadioChangeEvent } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useContextReports } from '../../../../../context/reports';
import { useReportLongTerm } from '../../../../../hooks/reports';
import { commafy } from '../../../../../utils/text';
import BarChart from '../../../../Common/Charts/BarChart';
import { IBarChartData } from '../../../../Common/Charts/types';
import MovingTo from '../../../../Common/MovingTo';
import TextWithLabel from '../../../../Common/TextWithLabel';
import ReportSection from '../Section';
import { longTermDatasets as datasets } from '../../constants';
import CustomInput from '../../../../Common/CustomInput';
import useDebounce from '../../../../../hooks/debounce';

interface ILongTermInvestments {
  provided?: DraggableProvided;
}

const LongTermInvestments = ({ provided }: ILongTermInvestments) => {
  const name = 'longTermInvestmentsNumbers';
  const [years, setYears] = useState<number>(15);
  const [data, setData] = useState<IBarChartData>();
  const [loanKey, setLoanKey] = useState<number>(0);

  const reportLongTerm = useReportLongTerm();

  const form = useFormInstance();
  const loans = useWatch('loans', form)
  const movingTo = useWatch(['movingTo', name], form);
  const isSectionActive = useWatch(['reportView', name, 'active'], form);
  const rateOfReturn = useWatch(['loans', loanKey, 'rateOfReturn'], form);
  const currentPropertyValue = useWatch(['loans', loanKey, 'currentPropertyValue'], form);
  const realEstateTaxes = useWatch(['loans', loanKey, 'realEstateTaxes'], form);
  const mortgageInsuranceDeduction = useWatch(['loans', loanKey, 'externalLoan', 'mortgageInsuranceDeduction'], form);
  const occupancyUse = useWatch(['loans', loanKey, 'externalLoan', 'occupancyUse'], form);

  const debouncedRateOfReturn = useDebounce(rateOfReturn, 500)
  const debouncedCurrentPropValue = useDebounce(currentPropertyValue, 500)
  const { handleLoansUpdate, isBorrower } = useContextReports()

  useEffect(() => {
    if (!loans || loanKey === undefined) return;
    if(!currentPropertyValue && !debouncedCurrentPropValue) return

    const updatedLoans = [...loans];
    const updatedLoan = updatedLoans[loanKey];

    if (debouncedRateOfReturn) {
      updatedLoan.rateOfReturn = String(debouncedRateOfReturn);
    }

    if (debouncedCurrentPropValue) {
      updatedLoan.currentPropertyValue = String(debouncedCurrentPropValue);
    }

    form.setFieldValue(['loans', loanKey, 'rateOfReturn'], updatedLoan.rateOfReturn);
    form.setFieldValue(['loans', loanKey, 'currentPropertyValue'], updatedLoan.currentPropertyValue);
  }, [debouncedRateOfReturn, debouncedCurrentPropValue]);

  useEffect(() => {
    if (!loans || !movingTo || !isSectionActive) return;
    const loan = loans.find((item: any) => item.id === movingTo);
    const loanIndex = loans.findIndex((item: any) => item.id === movingTo);

    if (loanIndex >= 0) {
      setLoanKey(loanIndex);
    }

    const delay = 800;
    const timeoutId = setTimeout(() => {
      if (!loan || !loan?.id) return;

      const payload = {
        years,
        ...(isBorrower && { rateOfReturn }),
        ...(isBorrower && { currentPropertyValue }),
        ...(isBorrower && { realEstateTaxes }),
        ...(isBorrower && { mortgageInsuranceDeduction }),
        ...(isBorrower && { occupancyUse })
      }

      reportLongTerm.fetch(payload, loan ? loan.id : loans[0].id).then((response) => {
        const labels = Array.from({ length: years }, (_, i) => moment().year() + i);

        const newData: IBarChartData = {
          labels: labels.filter((label, index) => (window.outerWidth < 768 ? index % 2 === 0 : true)),
          datasets: datasets.map((dataset) => ({
            name: dataset.name,
            label: dataset.label,
            data: (response?.data[`${dataset.name}Array`] || []).filter((label, index) =>
              window.outerWidth < 768 ? index % 2 === 0 : true
            ),
            maxBarThickness: 15,
            inflateAmount: 1,
            backgroundColor: dataset.color,
            borderRadius: {
              topLeft: 4,
              topRight: 4,
            },
            total: `$${commafy(response?.data.totals[`${dataset.name}Total`] || 0)}`,
            details: dataset.details,
          })),
        };

        setData(newData);
      });
    }, delay);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [loans, years, movingTo, isSectionActive, debouncedRateOfReturn, debouncedCurrentPropValue]);

  const handleYearsChange = (e: RadioChangeEvent) => {
    setYears(e.target.value);
  };

  // TODO: This gives negative value sometimes figure out why
  const getTotalGains = () => {
    const totals = reportLongTerm.data?.data.totals;

    if (!totals) return 0;

    const totalGains = Object.values(totals)
      .reduce((accumulator, currentValue) => accumulator + Number(currentValue), 0)
      .toFixed(0);

    return commafy(totalGains);
  };

  const handleBlur = () => {
    if (!isBorrower) {
      handleLoansUpdate?.();
    }
  }

  return (
    <ReportSection id={name} name={name} title="Long-Term Investments Numbers" provided={provided}>
      <Space style={{ padding: '10px' }} direction="vertical" size={20}>
        <Row>
          <Col md={6} span={24}>
            <TextWithLabel label={`Total gains after ${years} years`} value={`$${getTotalGains()}`} />
          </Col>
          <Col md={6} span={24}>
            <CustomInput
              inputClassName='bg-white'
              identifier={['loans', loanKey, 'rateOfReturn']}
              bordered
              suffix='%'
              placeholder="$0"
              formItemProps={{
                className: 'label-gray',
                label: 'Rate of Return',
                name: ['loans', loanKey, 'rateOfReturn'],
              }}
              rowStyles={{ width: "80%" }}
              onBlur={handleBlur}
            />
          </Col>
          <Col md={6} span={24}>
            <CustomInput
              inputClassName='bg-white'
              identifier={['loans', loanKey, 'currentPropertyValue']}
              bordered
              prefix="$"
              placeholder="$0"
              formItemProps={{
                className: 'label-gray',
                label: 'Current Property Value',
                name: ['loans', loanKey, 'currentPropertyValue'],
              }}
              rowStyles={{ width: "80%" }}
              onBlur={handleBlur}
            />
          </Col>
          <Col md={6} span={24}>
            <MovingTo name={name} />
          </Col>
        </Row>

        <BarChart
          labelFont={years === 15 ? 16 : 12}
          data={data} stacked reversed loading={!reportLongTerm.data && reportLongTerm.loading} showTotal withYearLabel>
          <Radio.Group onChange={handleYearsChange} value={years} style={{ marginBottom: 8 }} size="large">
            <Radio.Button value={15}>15 years</Radio.Button>
            <Radio.Button value={30}>30 years</Radio.Button>
          </Radio.Group>
        </BarChart>
      </Space>
    </ReportSection>
  );
};

LongTermInvestments.defaultProps = {
  provided: undefined,
};

export default LongTermInvestments;
